import Alert, { AlertColor } from '@mui/material/Alert'
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import upperFirst from 'lodash/upperFirst'
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface IToastApi {
  error: (message: string) => void
  info: (message: string) => void
  success: (message: string) => void
  warning: (message: string) => void
}

interface IToast {
  id: number
  message: string
  severity: AlertColor
}

// Errors and warnings often carry a reason worth reading, so they stay longer
const AUTO_HIDE_DURATIONS: Record<AlertColor, number> = { error: 6000, warning: 6000, info: 4000, success: 4000 }

const noop = () => undefined

// Outside a provider, e.g. a component rendered on its own in a test, toasts are dropped
const ToastContext = createContext<IToastApi>({ error: noop, info: noop, success: noop, warning: noop })

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const [toast, setToast] = useState<IToast | undefined>(undefined)
  const [isOpen, setOpen] = useState(false)

  const show = useCallback((message: string, severity: AlertColor) => {
    // A new id remounts the snackbar, so the same message shown twice restarts its timer
    setToast((previous) => ({ id: (previous?.id ?? 0) + 1, message, severity }))
    setOpen(true)
  }, [])

  const toastApi = useMemo(
    () => ({
      error: (message: string) => show(message, 'error'),
      info: (message: string) => show(message, 'info'),
      success: (message: string) => show(message, 'success'),
      warning: (message: string) => show(message, 'warning'),
    }),
    [show]
  )

  function handleClose(_?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) {
    if (reason === 'clickaway') {
      return
    }
    setOpen(false)
  }

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      <Snackbar
        key={toast?.id}
        open={isOpen}
        autoHideDuration={toast && AUTO_HIDE_DURATIONS[toast.severity]}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleClose}
          closeText={upperFirst(t('ToastProvider.close'))}
          severity={toast?.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 480, overflowWrap: 'anywhere' }}
        >
          <Typography variant="subtitle2" component="span" fontWeight={500}>
            {toast?.message}
          </Typography>
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export const useToast = (): IToastApi => useContext(ToastContext)
